import { Form, Tooltip } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { type BlockerFunction } from "react-router-dom"

import {
  type NestedKeys,
  isFormChanged
} from "../../../utils/object/is-form-changed.ts"
import ReactRouterPrompt from "../../ReactRouterPrompt.tsx"
import { FormChangedConfirm } from "./FormChangedConfirm.tsx"

interface FormChangedWatcherProps<T> {
  form: FormInstance<T>
  saving?: boolean
  hidden?: boolean
  debug?: boolean
  ignoreKeys?: Array<NestedKeys<T>>
  onDirtyChange?: (isDirty: boolean) => void
}

export function FormChangedWatcher<T>({
  form,
  saving,
  debug,
  hidden,
  ignoreKeys,
  onDirtyChange
}: FormChangedWatcherProps<T>) {
  const actual = Form.useWatch([], form)
  const initialValuesRef = useRef<T>(null)
  const [isDirty, setIsDirty] = useState(false)

  useEffect(() => {
    initialValuesRef.current = form.getFieldsValue()
  }, [saving, form])

  useEffect(() => {
    const initial = (initialValuesRef.current || {}) as T
    const isChanged = isFormChanged<T>(actual, initial, ignoreKeys, debug)

    const dirty = isChanged
    setIsDirty(dirty)
    onDirtyChange?.(dirty)
  }, [actual, saving])

  const blockNavigation = useCallback<BlockerFunction>(
    (args) => {
      if (!isDirty) return false
      const current = args?.currentLocation
      const next = args?.nextLocation
      if (!current || !next) return true
      return current.pathname !== next.pathname
    },
    [isDirty]
  )

  if (hidden) return null

  return (
    <>
      {!import.meta.env.PROD && (
        <Tooltip title={`Form dirty: ${isDirty}`}>
          <div
            className={`fixed ${isDirty ? "bg-red-500" : "bg-green-500"} size-6 rounded-full cursor-pointer z-50 bottom-16 right-0.5`}
          />
        </Tooltip>
      )}
      <ReactRouterPrompt when={blockNavigation}>
        {({ isActive, onConfirm, onCancel }) => (
          <FormChangedConfirm
            open={isActive}
            onOk={onConfirm}
            onCancel={onCancel}
          />
        )}
      </ReactRouterPrompt>
    </>
  )
}
